import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { firstValueFrom, timeout, TimeoutError } from 'rxjs';
import { environment } from '../../../environments/environment';

const CHECK_TOKEN_PATH = '/authorization/v2/oauth/check_token';

/**
 * How long introspection may take (PS-6495 M4). A timeout resolves as `failed`, which the guided
 * setup shows on its failure screen - the same as any other introspection failure.
 */
export const CHECK_TOKEN_TIMEOUT_MS = 15_000;

/**
 * One entry of `details.phoneServiceData`. The server sends `phoneServiceId`, `brandId`,
 * `contractId`, `type` and `description`; only what a caller reads is typed.
 *
 * `brandId` was added 2026-10-09: the legal links on the order screen live per brand in the
 * localization API, and this is the only place the brand is available to this app. The callnote
 * payload does not carry one, and the hostname gives a domain rather than an id.
 */
interface PhoneServiceEntry {
	phoneServiceId?: number | string;
	brandId?: number | string;
}

interface CheckTokenResponse {
	details?: {
		phoneServiceData?: PhoneServiceEntry[];
	};
}

export type PhoneServiceResolution =
	/**
	 * The token vouches for exactly one phone service. The only case a booking may proceed on.
	 *
	 * `brandId` is null when the entry carried none. That is NOT a failure — the booking needs only
	 * the phone service — it costs the brand's legal links, which the order screen then renders as
	 * plain text rather than guessing a brand.
	 */
	| { result: 'resolved'; phoneServiceId: number; brandId: number | null }
	/** The token vouches for no phone service at all. */
	| { result: 'none' }
	/** More than one, and nothing to choose by. Deliberately refused rather than guessed. */
	| { result: 'ambiguous'; count: number }
	/** Introspection failed, or the entry was unusable. Reason is for logging only. */
	| { result: 'failed'; reason: string };

/**
 * PS-6495 — asks the authorization server which phone service the customer's token vouches for.
 *
 * The booking endpoint takes a `phoneServiceId`, and this is its **only** source: never user
 * input, never the URL, never the callnote payload. Since PS-6646 the callnote does carry its
 * `phoneServiceId`, but only as the value the token's one is CHECKED against
 * (`GuidedSetup.resolvePhoneService`); what is booked against is still what this service resolves.
 *
 * **Why "exactly one" and not "the first one".** `details.phoneServiceData` is a list: the
 * authorization server builds it from `findByUserAccountId`, so it holds every phone service of the
 * account, and each entry is only `phoneServiceId`, `brandId`, `contractId`, `type`, `description`
 * — nothing that ties one of them to a particular callnote. The relation is 1:1 today (Torsten,
 * 2026-09-04), so in practice the list has one entry and this resolves.
 *
 * That assumption is load-bearing, so it is checked rather than trusted. Taking `[0]` would keep
 * working silently on the day an account gains a second phone service, and would book a chargeable
 * SIP-Account against the wrong contract. A refusal is recoverable; a wrong booking reaches an
 * invoice. So more than one entry is an outcome the caller has to handle, not a case this service
 * resolves.
 */
@Injectable({
	providedIn: 'root',
})
export class TokenIntrospectionService {
	private readonly httpClient: HttpClient = inject(HttpClient);

	async resolvePhoneServiceId(accessToken: string): Promise<PhoneServiceResolution> {
		// Form-urlencoded, per the introspection endpoint's contract. The bearer interceptor also
		// attaches the token as a header on this path, which is allowlisted and harmless — the
		// endpoint reads the body.
		const body = new HttpParams().set('token', accessToken);

		let response: CheckTokenResponse | undefined;
		try {
			response = await firstValueFrom(
				this.httpClient
					.post<CheckTokenResponse>(`${environment.baseUrl}${CHECK_TOKEN_PATH}`, body)
					.pipe(timeout(CHECK_TOKEN_TIMEOUT_MS)),
			);
		} catch (error: unknown) {
			if (error instanceof TimeoutError) {
				return { result: 'failed', reason: `introspection timed out after ${CHECK_TOKEN_TIMEOUT_MS} ms` };
			}
			const status = error instanceof HttpErrorResponse ? error.status : 0;
			return { result: 'failed', reason: `introspection refused (HTTP ${status})` };
		}

		const entries = response?.details?.phoneServiceData;
		if (!Array.isArray(entries) || entries.length === 0) {
			return { result: 'none' };
		}
		if (entries.length > 1) {
			return { result: 'ambiguous', count: entries.length };
		}

		// A Java long crosses the wire as a JSON number, but a string has been seen from other
		// services in this estate, so coerce and then insist on a usable value.
		const phoneServiceId = Number(entries[0]?.phoneServiceId);
		if (!Number.isFinite(phoneServiceId) || phoneServiceId <= 0) {
			return { result: 'failed', reason: 'phoneServiceData entry carried no usable id' };
		}

		// Coerced the same way and for the same reason, but NOT insisted on: a missing brand must not
		// fail a booking that has everything it needs.
		const rawBrandId = Number(entries[0]?.brandId);
		const brandId = Number.isFinite(rawBrandId) && rawBrandId > 0 ? rawBrandId : null;

		return { result: 'resolved', phoneServiceId, brandId };
	}
}
